import { cardFor } from "./learningEngine";
import type { LearningExercise, LearningPath, LearningPathId, LearningState } from "../types";

export interface RunSummary {
  answered: number;
  correct: number;
  newlyStable: string[];
  masteryDeltaPct: number;
  pathLabel: string;
}

function pathMatches(pathId: LearningPathId | "all", exerciseOrPathId: { pathId: LearningPathId } | LearningPathId): boolean {
  if (pathId === "all") return true;
  const candidate = typeof exerciseOrPathId === "string" ? exerciseOrPathId : exerciseOrPathId.pathId;
  return candidate === pathId;
}

function masteryPct(state: LearningState, exercises: LearningExercise[], pathId: LearningPathId | "all"): number {
  const scoped = exercises.filter((exercise) => pathMatches(pathId, exercise));
  if (!scoped.length) return 0;
  const total = scoped.reduce((sum, exercise) => sum + cardFor(state, exercise.id).mastery, 0);
  return Math.round((total / scoped.length) * 20);
}

export function summarizeRun(
  before: LearningState,
  after: LearningState,
  exercises: LearningExercise[],
  pathId: LearningPathId | "all",
  paths: LearningPath[]
): RunSummary {
  const exerciseById = new Map(exercises.map((exercise) => [exercise.id, exercise]));
  const addedSessions = after.sessions.slice(before.sessions.length).filter((session) => pathMatches(pathId, session.pathId));
  const newlyStable = Object.entries(after.cards)
    .filter(([exerciseId, card]) => {
      const exercise = exerciseById.get(exerciseId);
      if (!exercise || !pathMatches(pathId, exercise)) return false;
      return (before.cards[exerciseId]?.mastery ?? 1) < 4 && card.mastery >= 4;
    })
    .map(([exerciseId]) => exerciseId);

  const beforePct = masteryPct(before, exercises, pathId);
  const afterPct = masteryPct(after, exercises, pathId);

  return {
    answered: addedSessions.length,
    correct: addedSessions.filter((session) => session.correct).length,
    newlyStable,
    masteryDeltaPct: afterPct - beforePct,
    pathLabel: pathId === "all" ? "Alle Pfade" : paths.find((path) => path.id === pathId)?.label ?? pathId,
  };
}
